import {useEffect, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import TicketsService from '../../../../services/ticket.service'
import AccountService from '../../../../services/account.service'
import ProviderService from '../../../../services/provider.service'

const CreateTicketPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [createSuccess, setCreateSuccess] = useState('')
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    productType: '',
    orderId: '',
    providerId: '',
    assignedTo: '',
    userId: '',
  })
  const [user, setUser] = useState<any>(null)
  const [userSearch, setUserSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const [userNotFound, setUserNotFound] = useState(false)
  const [providers, setProviders] = useState<any[]>([])

  const fetchProviders = async () => {
    const response = await ProviderService.getProviderForCustomers()
    setProviders(response.data.returnData)
  }

  const handleSubmit = async () => {
    try {
      setCreating(true)
      setCreateError('')
      setCreateSuccess('')
      // console.log({form, user})
      await TicketsService.createTicket({
        title: form.title,
        description: form.description,
        priority: form.priority,
        productType: form.productType,
        orderId: form.orderId,
        providerId: form.providerId,
        assignedTo: form.assignedTo,
        userId: user.id,
      })
      setCreateSuccess('Ticket created successfully')
      setTimeout(() => {
        navigate('/tickets/list')
      }, 1500)
    } catch (e: any) {
      setCreateError('Failed to create ticket')
    } finally {
      setCreating(false)
    }
  }

  const handleCancel = () => {
    navigate('/tickets/list')
  }

  const handleUserSearch = async () => {
    if (!userSearch.trim()) {
      setUserNotFound(false)
      setUser(null)
      return
    }

    try {
      setSearching(true)
      setUserNotFound(false)
      setUser(null)

      const response = await AccountService.getUser(userSearch.trim())
      if (response.data) {
        setUser(response.data)
        setUserNotFound(false)
      } else {
        setUser(null)
        setUserNotFound(true)
      }
    } catch (error) {
      setUser(null)
      setUserNotFound(true)
    } finally {
      setSearching(false)
    }
  }

  // Fetch user by ID from query parameters
  const fetchUserById = async (userId: string) => {
    try {
      setSearching(true)
      const response = await AccountService.getProfile(userId)
      if (response.data) {
        setUser(response.data)
        setUserNotFound(false)
      } else {
        setUser(null)
        setUserNotFound(true)
      }
    } catch (error) {
      setUser(null)
      setUserNotFound(true)
    } finally {
      setSearching(false)
    }
  }

  useEffect(() => {
    fetchProviders()

    // Get userId and orderId from query parameters
    const userId = searchParams.get('userId')
    const orderId = searchParams.get('orderId')

    // console.log('userId', userId)
    // console.log('orderId', orderId)

    // Set orderId in form if provided
    if (orderId) {
      setForm((prev) => ({...prev, orderId}))
    }

    // Fetch user if userId is provided, otherwise set to null
    if (userId) {
      fetchUserById(userId)
    } else {
      setUser(null)
      setUserNotFound(false)
    }
  }, [searchParams])

  return (
    <KTCard>
      <div className='card-header border-0 pt-6'>
        <div className='card-toolbar d-flex w-100 justify-content-between'>
          <h3 className='card-title'>Create New Ticket</h3>
          <button type='button' className='btn btn-light' onClick={handleCancel}>
            Back to Tickets
          </button>
        </div>
      </div>
      <KTCardBody className='py-4'>
        {createError && <div className='alert alert-danger'>{createError}</div>}
        {createSuccess && <div className='alert alert-success'>{createSuccess}</div>}

        <div className='row'>
          <div className='col-lg-12'>
            <div className='mb-5'>
              <label className='form-label required'>Title</label>
              <input
                type='text'
                className='form-control form-control-solid'
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                placeholder='Enter ticket title'
              />
            </div>

            <div className='mb-5'>
              <label className='form-label required'>Description</label>
              <textarea
                className='form-control form-control-solid'
                rows={6}
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder='Enter detailed description of the issue'
              />
            </div>

            <div className='row'>
              <div className='col-md-6'>
                <div className='mb-5'>
                  <label className='form-label required'>Priority</label>
                  <select
                    className='form-select form-select-solid'
                    value={form.priority}
                    onChange={(e) => setForm({...form, priority: e.target.value})}
                  >
                    <option value='Low'>Low</option>
                    <option value='Medium'>Medium</option>
                    <option value='High'>High</option>
                  </select>
                </div>
              </div>

              <div className='col-md-6'>
                <div className='mb-5'>
                  <label className='form-label required'>Product Type</label>
                  <select
                    className='form-select form-select-solid'
                    value={form.productType}
                    onChange={(e) => setForm({...form, productType: e.target.value})}
                  >
                    <option value=''>Select Product Type</option>
                    <option value='LocalSim'>LocalSim</option>
                    <option value='InternationalEsim'>InternationalEsim</option>
                    <option value='PrepaidCards'>PrepaidCards</option>
                    <option value='VisitorSim'>VisitorSim</option>
                  </select>
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-md-6'>
                <div className='mb-5'>
                  <label className='form-label required'>Order ID</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    value={form.orderId}
                    onChange={(e) => setForm({...form, orderId: e.target.value})}
                    placeholder='Enter order ID'
                  />
                </div>
              </div>
              <div className='col-md-6'>
                <div className='mb-5'>
                  <label className='form-label required'>Provider</label>
                  <select
                    className='form-select form-select-solid'
                    value={form.providerId}
                    onChange={(e) => setForm({...form, providerId: e.target.value})}
                  >
                    <option value=''>Select Provider</option>
                    {providers.length > 0 &&
                      providers.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-md-6'>
                <div className='mb-5'>
                  <label className='form-label required'>Assigned To</label>
                  <select
                    className='form-select form-select-solid'
                    value={form.assignedTo}
                    onChange={(e) => setForm({...form, assignedTo: e.target.value})}
                  >
                    <option value=''>Select Assignment</option>
                    <option value='tygo'>Tygo</option>
                    <option value='customer_service'>Customer Service</option>
                    <option value='provider'>Provider</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Customer Search Section */}
            <div className='mb-5'>
              <div className='row'>
                <div className='col-md-8'>
                  <label className='form-label required'>Search Customer (Email or Phone)</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder='Enter email or phone number'
                  />
                </div>
                <div className='col-md-4 d-flex align-items-end'>
                  <button
                    type='button'
                    className='btn btn-primary w-100'
                    onClick={handleUserSearch}
                    disabled={searching || !userSearch.trim()}
                  >
                    {searching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {/* User Found Display */}
              {user && (
                <div className='mt-3'>
                  <div className='alert alert-success d-flex align-items-center'>
                    <i className='fas fa-check-circle me-2'></i>
                    <div>
                      <strong>Customer Found:</strong> {user.firstName} {user.lastName}
                      <br />
                      <small>
                        Email: {user.email} | Phone: {user.phoneNumber}
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* User Not Found Display */}
              {userNotFound && (
                <div className='mt-3'>
                  <div className='alert alert-warning d-flex align-items-center'>
                    <i className='fas fa-exclamation-triangle me-2'></i>
                    <div>
                      <strong>Customer not found</strong> - Please check the email or phone number
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className='d-flex justify-content-start gap-3 mt-15'>
              <button className='btn btn-light' onClick={handleCancel} disabled={creating}>
                Cancel
              </button>
              <button
                className='btn btn-primary'
                disabled={
                  creating ||
                  !form.title ||
                  !form.description ||
                  !form.productType ||
                  !form.orderId ||
                  !form.providerId ||
                  !form.assignedTo ||
                  !user
                }
                onClick={handleSubmit}
              >
                {creating ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </div>

          {/* <div className='col-lg-4'>
            <div className='card bg-light-info'>
              <div className='card-body'>
                <h5 className='card-title'>Tips for creating tickets</h5>
                <ul className='list-unstyled'>
                  <li className='mb-2'>
                    <i className='fas fa-check text-success me-2'></i>
                    Be specific and clear in your title
                  </li>
                  <li className='mb-2'>
                    <i className='fas fa-check text-success me-2'></i>
                    Provide detailed description of the issue
                  </li>
                  <li className='mb-2'>
                    <i className='fas fa-check text-success me-2'></i>
                    Choose appropriate priority level
                  </li>
                  <li className='mb-2'>
                    <i className='fas fa-check text-success me-2'></i>
                    Include relevant product information
                  </li>
                </ul>
              </div>
            </div>
          </div> */}
        </div>
      </KTCardBody>
    </KTCard>
  )
}

export default CreateTicketPage
